/**
 * People merge (X.3.7 Part 2).
 *
 * Joins the staff_roster (HR roster + Slice A linking) with the user_tenants
 * accounts (Team membership) into one row-per-person model for the "People ▸
 * Everyone" view. The join is on staff_roster.linked_user_id === users.id.
 *
 * Three kinds, all SURFACED (the merge's payoff):
 *   - linked       — a roster row with a linked account (operating role +
 *                    access role both shown; they can legitimately differ).
 *   - roster_only  — a roster row with no linked account ("No login · Link…").
 *   - account_only — an account with no roster row ("Needs attention": a login
 *                    exists but no person record, e.g. duplicate logins). Today
 *                    these hide in Team while the real person hides in Staff.
 *
 * Pure + side-effect free so it can be unit-tested against production-seed
 * shapes. Both inputs are already tenant-scoped (RLS) by their endpoints.
 */

import type { StaffLinkRow } from './staffAdmin';
import type { TeamMember } from './team';
import type { UserTenantRole } from './types';
import { ROLE_DISPLAY } from './teamRoles';

export type PersonKind = 'linked' | 'roster_only' | 'account_only';

export interface PersonRow {
  key: string;
  kind: PersonKind;
  // Roster (HR) side — null for account_only.
  roster_id: string | null;
  full_name: string | null;
  billable_role: string | null;
  // Roster/team-grouping unit. null for account_only rows or when none set
  // (the Team roster's "No unit yet" group). Editable in the drawer.
  unit_id: string | null;
  unit_name: string | null;
  preferred_name: string | null;
  // Report-signatory credentials (PR-B), e.g. "BSW, CSW, CMC". null for
  // account_only rows or when none on file.
  credentials: string | null;
  // Slice 3c — CM bio (owner path). null for account_only rows or none on file.
  bio: string | null;
  // 0239 — the lines printed under the name; null = none.
  signature_block?: string | null;
  // 0161 — CM direct phone for the intro email. null for account_only rows or
  // none on file (the copied block renders a highlighted [Phone] placeholder).
  phone: string | null;
  // Contracted weekly-hours target as numeric text; null for account_only
  // rows or when none is set (the capacity board says so plainly).
  target_weekly_hours: string | null;
  // Slice 3c — whether a photo is on file (false for account_only rows).
  has_photo: boolean;
  // Per-staff SPICE flag (0134; false for account_only rows — no roster
  // record, no flag). Drives the People-tab "Does SPICE" toggle.
  does_spice: boolean;
  // Per-staff care-management flag (0143; false for account_only rows).
  // Drives the People-tab "Does care management" toggle, the does_spice twin.
  does_care_management: boolean;
  // Directory fields (0176), as the server projected them for this caller
  // (the private pair is null unless view_staff_private_contact). null for
  // account_only rows.
  birthday_month: number | null;
  birthday_day: number | null;
  work_anniversary: string | null;
  start_date: string | null;
  home_address: string | null;
  /** The home's five parts (0228); null on every part: text only. */
  home_address_line1?: string | null;
  home_address_line2?: string | null;
  home_address_city?: string | null;
  home_address_state?: string | null;
  home_address_zip?: string | null;
  // The map home point's town and ZIP (private-tier with the address).
  home_city: string | null;
  home_zip: string | null;
  personal_phone: string | null;
  // Roster archive state: false = "former staff" (archived). null for
  // account_only rows (no roster record). The list hides archived rows by
  // default; a "Show former staff" toggle reveals them with a Restore action.
  roster_is_active: boolean | null;
  roster_email: string | null;
  // Suggested email-match for an unlinked roster row.
  suggested_user_id: string | null;
  suggested_user_email: string | null;
  suggested_user_display_name: string | null;
  suggested_ambiguous: boolean;
  // Account (access) side — null for roster_only.
  user_id: string | null;
  account_email: string | null;
  account_display_name: string | null;
  access_role: UserTenantRole | null;
  account_is_active: boolean | null;
  last_sign_in_at: string | null;
}

/**
 * user_tenants.role in plain words (X.3.7 Part 2).
 *
 * COLLAPSED 2026-08-27 into ROLE_DISPLAY. This function used to hold a
 * SECOND, competing vocabulary — 'Clinical Team' where ROLE_DISPLAY said
 * 'Primary CM', 'Office support' where it said 'Admin support' — on the
 * stated ground that it communicated "what the access level MEANS, not
 * the internal role name". The Team-tab role picker then rendered both
 * at once, separated by a middot ("Clinical Team · Primary CM"), which
 * is two names for one seat on the screen where you assign it.
 *
 * The founder ruling that settled the vocabulary largely CHOSE this set
 * ('Clinical Team' and 'Supervisor' survived essentially intact), so the
 * collapse keeps the better names and drops the duplication. The
 * "what it means" job moved to ROLE_BLURB, which is a full sentence and
 * can carry what a two-word label never could — including the
 * "Read-only" that mso_staff's label used to say on its own.
 *
 * Kept as a named export rather than inlined: it is the access-column
 * reading of a role, and the unknown-role fallback differs from
 * displayRole's (which falls through to the raw key).
 */
export function accessRoleLabel(role: UserTenantRole | null | undefined): string {
  if (!role) return 'Unknown access';
  return role in ROLE_DISPLAY ? ROLE_DISPLAY[role] : 'Unknown access';
}

/** staff_roster.billable_role (the "designation" / operating role). Mirrors the
 *  StaffRosterBillableRole enum (schema.ts) + the server tuple
 *  (repos/staff STAFF_ROSTER_BILLABLE_ROLES). */
export type BillableRole = 'care_manager' | 'spice_therapist' | 'admin' | 'manager' | 'owner';

const BILLABLE_ROLE_LABELS: Record<string, string> = {
  care_manager: 'Care Manager',
  spice_therapist: 'SPICE Therapist',
  admin: 'Admin',
  manager: 'Manager',
  owner: 'Owner',
};

/** Ordered designation options for the People-tab "Change role" picker.
 *  Tenant-neutral role tokens (not Peace-specific data). */
export const BILLABLE_ROLES: readonly BillableRole[] = [
  'care_manager',
  'spice_therapist',
  'manager',
  'admin',
  'owner',
];

/** staff_roster.billable_role (operating role) in display form. */
export function billableRoleLabel(role: string | null | undefined): string {
  if (!role) return 'No role';
  return BILLABLE_ROLE_LABELS[role] ?? role;
}

/** One-line "what is this role?" for the Change-role picker, the
 *  billable_role twin of teamRoles' roleBlurb. */
const BILLABLE_ROLE_BLURB: Record<BillableRole, string> = {
  care_manager: 'Does care management and logs care-management rates.',
  spice_therapist: 'Runs SPICE sessions and logs SPICE rates.',
  admin: 'Office staff; no billable rates.',
  manager: 'Supervises care managers and does care management.',
  owner: 'Runs the business.',
};

/** Blurb for a billable role, the designation twin of teamRoles' roleBlurb. */
export function billableRoleBlurb(role: BillableRole): string {
  return BILLABLE_ROLE_BLURB[role];
}

/** The rate-band phrase a designation change turns on, mirroring the
 *  server's flagsImpliedByBillableRole VERBATIM (repos/staff.ts):
 *  care_manager | manager -> care-management rates; spice_therapist ->
 *  SPICE rates; admin + owner imply neither. null means the change implies
 *  no rate flag, so the Change-role sheet says nothing extra about it. */
export function impliedRatePhrase(role: BillableRole): string | null {
  switch (role) {
    case 'care_manager':
    case 'manager':
      return 'log care-management rates';
    case 'spice_therapist':
      return 'log SPICE rates';
    case 'admin':
    case 'owner':
      return null;
  }
}

/** The two per-person capability flags (staff_roster.does_care_management +
 *  does_spice): what this person may log, and so what they are to a family. */
export interface CapabilityFlags {
  does_care_management: boolean;
  does_spice: boolean;
}

/**
 * The role noun for one person, from their capability flags: what the People
 * tab and the intro email call them in prose ("will be the care manager
 * working directly with you"). A person is named for what they can log, both
 * nouns when both flags are on, and "team member" when neither is, so nobody
 * is called a care manager by default.
 */
export function roleNoun(flags: CapabilityFlags): string {
  if (flags.does_care_management && flags.does_spice) return 'care manager and SPICE therapist';
  if (flags.does_care_management) return 'care manager';
  if (flags.does_spice) return 'SPICE therapist';
  return 'team member';
}

/**
 * The person's work contact email for client-facing artifacts (the 0161 intro
 * email's "Email." line), picked from the candidates in priority order.
 *
 * Founder report 2026-07-25: the generated intro email rendered a highlighted
 * [Email] placeholder for a care manager whose address was on screen one line
 * above it. The roster's own `email` column is frequently blank while the
 * LINKED ACCOUNT carries the real work address (it is the address they sign in
 * with), so the roster column alone is not the person's email. Callers pass
 * roster-first, account-second.
 *
 * A blank/whitespace string counts as "not on file", so a stray '' never beats
 * a real address. null when no candidate is on file — the template then keeps
 * its [Email] placeholder, which is the honest answer for a roster person with
 * no account: there is genuinely nothing to fill in.
 */
export function contactEmail(...candidates: (string | null | undefined)[]): string | null {
  for (const candidate of candidates) {
    const trimmed = (candidate ?? '').trim();
    if (trimmed.length > 0) return trimmed;
  }
  return null;
}

function sortName(r: PersonRow): string {
  return (r.full_name ?? r.account_display_name ?? r.account_email ?? '').toLowerCase();
}

/**
 * Free-text match for the People-list filter. Searches the facts a person is
 * findable BY: their name, their sign-in address, their roster email, and both
 * role labels (operating designation + access level), so "care manager" or
 * "owner" narrows the list just as a name does. `query` must already be
 * lower-cased and trimmed; an empty query matches everyone (callers skip the
 * filter entirely in that case, so this is the safe answer either way).
 */
export function personMatchesQuery(row: PersonRow, query: string): boolean {
  if (query.length === 0) return true;
  const haystack = [
    row.full_name,
    row.preferred_name,
    row.account_display_name,
    row.account_email,
    row.roster_email,
    billableRoleLabel(row.billable_role),
    row.access_role ? accessRoleLabel(row.access_role) : null,
  ]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

/**
 * Merge roster rows + tenant members into one row per person.
 * Real staff (linked / roster_only) sort first — they are what unit leads use
 * for bios, photos and assignment context. account_only ("needs attention")
 * login stubs sort LAST; the People tab renders them in their own quiet
 * collapsed section at the bottom (founder cleanup 2026-07-25 — these rows
 * used to pin to the top and bury the real staff).
 */
export function mergePeople(staff: StaffLinkRow[], members: TeamMember[]): PersonRow[] {
  const memberByUserId = new Map<string, TeamMember>();
  for (const m of members) memberByUserId.set(m.user_id, m);

  const linkedUserIds = new Set<string>();
  for (const s of staff) {
    if (s.linked_user_id) linkedUserIds.add(s.linked_user_id);
  }

  const rows: PersonRow[] = [];

  // 1. One row per roster member (linked or roster_only).
  for (const s of staff) {
    const account = s.linked_user_id ? memberByUserId.get(s.linked_user_id) : undefined;
    const kind: PersonKind = s.linked_user_id ? 'linked' : 'roster_only';
    rows.push({
      key: `roster:${s.id}`,
      kind,
      roster_id: s.id,
      full_name: s.full_name,
      billable_role: s.billable_role,
      unit_id: s.unit_id ?? null,
      unit_name: s.unit_name ?? null,
      preferred_name: s.preferred_name,
      credentials: s.credentials,
      bio: s.bio,
      signature_block: s.signature_block ?? null,
      phone: s.phone ?? null,
      target_weekly_hours: s.target_weekly_hours ?? null,
      has_photo: s.has_photo,
      does_care_management: s.does_care_management === true,
      // === true normalizes an older cached wire shape (no does_spice key)
      // to a real boolean — the toggle is a controlled checkbox.
      does_spice: s.does_spice === true,
      birthday_month: s.birthday_month ?? null,
      birthday_day: s.birthday_day ?? null,
      work_anniversary: s.work_anniversary ?? null,
      start_date: s.start_date ?? null,
      home_address: s.home_address ?? null,
      home_address_line1: s.home_address_line1 ?? null,
      home_address_line2: s.home_address_line2 ?? null,
      home_address_city: s.home_address_city ?? null,
      home_address_state: s.home_address_state ?? null,
      home_address_zip: s.home_address_zip ?? null,
      home_city: s.home_city ?? null,
      home_zip: s.home_zip ?? null,
      personal_phone: s.personal_phone ?? null,
      roster_is_active: s.is_active,
      roster_email: s.email,
      suggested_user_id: s.suggested_user_id,
      suggested_user_email: s.suggested_user_email,
      suggested_user_display_name: s.suggested_user_display_name,
      suggested_ambiguous: s.suggested_ambiguous,
      user_id: s.linked_user_id,
      // Prefer the live member record; fall back to the roster's denormalized
      // link fields if the member is not in the list (defensive).
      account_email: account?.email ?? s.linked_user_email,
      account_display_name: account?.display_name ?? s.linked_user_display_name,
      access_role: account?.role ?? null,
      account_is_active: account?.is_active ?? null,
      last_sign_in_at: account?.last_sign_in_at ?? null,
    });
  }

  // 2. One row per account with NO roster row ("needs attention").
  for (const m of members) {
    if (linkedUserIds.has(m.user_id)) continue;
    rows.push({
      key: `account:${m.user_id}`,
      kind: 'account_only',
      roster_id: null,
      full_name: null,
      billable_role: null,
      unit_id: null,
      unit_name: null,
      preferred_name: null,
      credentials: null,
      bio: null,
      phone: null,
      target_weekly_hours: null,
      has_photo: false,
      does_spice: false,
      does_care_management: false,
      birthday_month: null,
      birthday_day: null,
      work_anniversary: null,
      start_date: null,
      home_address: null,
      home_city: null,
      home_zip: null,
      personal_phone: null,
      roster_is_active: null,
      roster_email: null,
      suggested_user_id: null,
      suggested_user_email: null,
      suggested_user_display_name: null,
      suggested_ambiguous: false,
      user_id: m.user_id,
      account_email: m.email,
      account_display_name: m.display_name,
      access_role: m.role,
      account_is_active: m.is_active,
      last_sign_in_at: m.last_sign_in_at,
    });
  }

  // Staff first, account_only login stubs last, then by name within each group.
  return rows.sort((a, b) => {
    if (a.kind === 'account_only' && b.kind !== 'account_only') return 1;
    if (b.kind === 'account_only' && a.kind !== 'account_only') return -1;
    return sortName(a).localeCompare(sortName(b));
  });
}
